import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useCallback, useEffect, useState } from "react";
import { businessClientUseCase } from "../../../di/container";
import { useParams, useNavigate } from "react-router-dom";
import Table from "../../components/UI/Table";
import type { BusinessClientDetail as BusinessClientDetailType } from "../../../domain/types/business-client";
import type { OrderFile } from "../../../domain/types/business-client";

const InfoRow: React.FC<{ label: string; value: string | null | undefined }> = ({
  label,
  value,
}) => (
  <div className="flex border-b border-[#f0f0f0] py-[12px]">
    <div className="w-[140px] text-[#888888] font-medium text-[13px] shrink-0">{label}</div>
    <div className="text-[#333333] text-[13px]">{value || "-"}</div>
  </div>
);

const BusinessClientDetailPage: React.FC = () => {
  const { companyId, userId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<BusinessClientDetailType | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
  const [activeTab, setActiveTab] = useState<"info" | "orders" | "files">("info");

  const loadDetail = useCallback(async () => {
    if (!companyId || !userId) return;
    try {
      const result = await businessClientUseCase.getBusinessClientDetail(
        Number(companyId),
        Number(userId),
      );
      if (result) {
        setDetail(result);
      }
    } catch (error) {
      console.error("기업고객 상세 조회 실패:", error);
    }
  }, [companyId, userId]);

  const loadOrders = useCallback(async () => {
    if (!companyId || !userId) return;
    try {
      const result = await businessClientUseCase.getBusinessClientOrders(
        Number(companyId),
        Number(userId),
        { page: 0, size: 50 },
      );
      setOrders(result.items);
    } catch (error) {
      console.error("기업고객 주문내역 조회 실패:", error);
    }
  }, [companyId, userId]);

  const loadOrderFiles = useCallback(async () => {
    if (!companyId || !userId) return;
    try {
      const result = await businessClientUseCase.getBusinessClientOrderFiles(
        Number(companyId),
        Number(userId),
        0,
        50,
      );
      setOrderFiles(result.items);
    } catch (error) {
      console.error("기업고객 주문 파일 조회 실패:", error);
    }
  }, [companyId, userId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (activeTab === "orders") loadOrders();
    if (activeTab === "files") loadOrderFiles();
  }, [activeTab, loadOrders, loadOrderFiles]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "-";
    return new Date(dateStr)
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/-/g, ". ");
  };

  const tabClass = (tab: string) =>
    `px-[20px] py-[10px] text-[14px] font-medium cursor-pointer border-b-2 ${
      activeTab === tab
        ? "border-[#4285F4] text-[#4285F4]"
        : "border-transparent text-[#888888] hover:text-[#555555]"
    }`;

  return (
    <div className="relative">
      <CompanyAdminLayout>
        <div className="p-5">
          {/* 헤더 */}
          <div className="mb-[28px] flex items-center gap-[12px]">
            <button
              onClick={() => navigate(`/company/${companyId}/business-clients`)}
              className="text-[#888888] hover:text-[#555555] text-[14px]"
            >
              &larr; 목록으로
            </button>
            <h2 className="text-[1.375rem] font-semibold">
              {detail?.businessName || detail?.name || "기업고객 상세"}
            </h2>
          </div>

          {/* 탭 */}
          <div className="flex border-b border-[#e0e0e0] mb-[20px]">
            <div className={tabClass("info")} onClick={() => setActiveTab("info")}>
              기업 정보
            </div>
            <div className={tabClass("orders")} onClick={() => setActiveTab("orders")}>
              주문 내역
            </div>
            <div className={tabClass("files")} onClick={() => setActiveTab("files")}>
              주문 파일
            </div>
          </div>

          {/* 기업 정보 탭 */}
          {activeTab === "info" && detail && (
            <div className="bg-white border border-[#d9d9d9] rounded-[0.625rem] p-[1.25rem]">
              <h3 className="text-[16px] font-bold mb-[16px]">회원 정보</h3>
              <InfoRow label="이름" value={detail.name} />
              <InfoRow label="이메일" value={detail.email} />
              <InfoRow label="연락처" value={detail.mobile} />
              <InfoRow label="가입일" value={formatDate(detail.createdAt)} />

              <h3 className="text-[16px] font-bold mt-[28px] mb-[16px]">기업 정보</h3>
              <InfoRow label="업체명" value={detail.businessName} />
              <InfoRow label="사업자번호" value={detail.businessNumber} />
              <InfoRow label="업종" value={detail.businessType} />
              <InfoRow label="업태" value={detail.businessCategory} />
              <InfoRow label="대표 연락처" value={detail.businessPhone} />
              <InfoRow label="우편번호" value={detail.postcode} />
              <InfoRow label="주소" value={detail.address} />
              <InfoRow label="상세주소" value={detail.addressDetail} />

              <h3 className="text-[16px] font-bold mt-[28px] mb-[16px]">세금계산서 정보</h3>
              <InfoRow label="담당자명" value={detail.taxManagerName} />
              <InfoRow label="담당자 연락처" value={detail.taxManagerPhone} />
              <InfoRow label="세금계산서 이메일" value={detail.taxEmail} />
            </div>
          )}

          {/* 주문 내역 탭 */}
          {activeTab === "orders" && (
            <div className="text-[12px]">
              <Table
                columnWidths={["80px", "120px", "180px", "100px", "120px", "120px"]}
                tabledata={{
                  head: [
                    <>주문번호</>,
                    <>주문명</>,
                    <>구매자</>,
                    <>상태</>,
                    <>금액</>,
                    <>주문일</>,
                  ],
                  body: orders.map((order) => [
                    <>{order.orderNumber || "-"}</>,
                    <>{order.name || "-"}</>,
                    <>{order.purchaserName || "-"}</>,
                    <>{order.orderStatus || "-"}</>,
                    <>{order.orderTotalAmount?.toLocaleString() || "0"}원</>,
                    <>{formatDate(order.createdAt)}</>,
                  ]),
                }}
              />
              {orders.length === 0 && (
                <div className="text-center text-[#888888] py-[40px] text-[14px]">
                  주문 내역이 없습니다.
                </div>
              )}
            </div>
          )}

          {/* 주문 파일 탭 */}
          {activeTab === "files" && (
            <div className="text-[12px]">
              <Table
                columnWidths={["80px", "120px", "180px", "200px", "120px"]}
                tabledata={{
                  head: [
                    <>주문번호</>,
                    <>주문명</>,
                    <>파일</>,
                    <>주문일</>,
                  ],
                  body: orderFiles.map((file) => [
                    <>{file.orderNumber || "-"}</>,
                    <>{file.orderName || "-"}</>,
                    <>
                      {file.fileUrl ? (
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#4285F4] hover:underline"
                        >
                          다운로드
                        </a>
                      ) : (
                        "-"
                      )}
                    </>,
                    <>{formatDate(file.createdAt)}</>,
                  ]),
                }}
              />
              {orderFiles.length === 0 && (
                <div className="text-center text-[#888888] py-[40px] text-[14px]">
                  주문 파일이 없습니다.
                </div>
              )}
            </div>
          )}
        </div>
      </CompanyAdminLayout>
    </div>
  );
};

export default BusinessClientDetailPage;